import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

/**
 * Global navigation service for utility modules that don't have direct access to useRouter
 * This allows auth store, axios interceptors, and other utilities to trigger navigation
 */
class NavigationService {
  private router: AppRouterInstance | null = null

  /**
   * Register the router instance (called from the root layout or main component)
   */
  setRouter(router: AppRouterInstance): void {
    this.router = router
  }

  /**
   * Navigate to a route using the registered router
   */
  push(href: string): void {
    if (this.router) {
      this.router.push(href)
    } else {
      // Fallback to window.location if router is not available
      // This should rarely happen if properly initialized
      console.warn('NavigationService: Router not registered, falling back to window.location')
      if (typeof window !== 'undefined') {
        window.location.href = href
      }
    }
  }

  /**
   * Replace the current route using the registered router
   */
  replace(href: string): void {
    if (this.router) {
      this.router.replace(href)
    } else {
      console.warn('NavigationService: Router not registered, falling back to window.location')
      if (typeof window !== 'undefined') {
        window.location.replace(href)
      }
    }
  }

  /**
   * Navigate back in history
   */
  back(): void {
    if (this.router) {
      this.router.back()
    } else {
      console.warn('NavigationService: Router not registered, falling back to window.history')
      if (typeof window !== 'undefined') {
        window.history.back()
      }
    }
  }

  /**
   * Check if router is available
   */
  isAvailable(): boolean {
    return this.router !== null
  }

  /**
   * Auth-aware navigation methods
   * These methods integrate with the authentication system
   */

  /**
   * Navigate after successful login
   */
  navigateAfterLogin(redirectTo?: string): void {
    const targetPath = redirectTo || '/dashboard'

    if (process.env.NODE_ENV === 'production') {
      console.log('NavigationService: Navigating after login', { targetPath })
    }

    this.push(targetPath)
  }

  /**
   * Navigate after logout
   */
  navigateAfterLogout(): void {
    if (process.env.NODE_ENV === 'production') {
      console.log('NavigationService: Navigating after logout')
    }

    this.push('/magic-login')
  }

  /**
   * Navigate to login with optional redirect
   */
  navigateToLogin(redirectPath?: string): void {
    const loginUrl = redirectPath
      ? `/magic-login?redirect=${encodeURIComponent(redirectPath)}`
      : '/magic-login'

    if (process.env.NODE_ENV === 'production') {
      console.log('NavigationService: Navigating to login', { loginUrl, redirectPath })
    }

    this.push(loginUrl)
  }

  /**
   * Navigate to dashboard
   */
  navigateToDashboard(): void {
    if (process.env.NODE_ENV === 'production') {
      console.log('NavigationService: Navigating to dashboard')
    }

    this.push('/dashboard')
  }
}

// Create a singleton instance
export const navigationService = new NavigationService()

// Export the service as default
export default navigationService
